import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, debounce, Divider, FormControl, InputAdornment, OutlinedInput, Stack } from '@mui/material';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';
import { CampaignFinanceLaw } from '../../../types/legal/campaignFinanceLaw';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';

const CampaignFinanceLaws = (): JSX.Element => {

    document.title = 'OCPF - Campaign Finance Law';

    const { showBoundary } = useErrorBoundary();      
    const [laws, setLaws] = useState<CampaignFinanceLaw[]>([]);
    const [allLaws, setAllLaws] = useState<CampaignFinanceLaw[]>([]);
    const [expandAll, setExpandAll] = useState(false);
    const [selectedLawId, setSelectedLawId] = useState(0);
    const [searchInput, setSearchInput] = useState('');

    useEffect(() => {
        getCampaignFinanceLaw();
    }, [])

  const getCampaignFinanceLaw = async () => {

    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/legal/law`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get the campaign finance laws.'));
        }
        return result.json();
      })
      .then(result => {
        let count = result.length;
        if (count > 0){
            setLaws(result);
            setAllLaws(result);
        }
      });
    }catch(error){
        console.error('Uncaught Error:', error);
        showBoundary(new Error(`${error}`));
    }
  }

 const expandLaw = (id: number) => {
   setSelectedLawId (selectedLawId===0?id:0);
 }

  const filterByName = debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        setSearchInput(searchInput);
        if(searchInput.length === 0){
          setLaws(allLaws);
          setExpandAll(false);
        }
        else{
            let filteredLaws = allLaws.filter((law: any) => law.title.toLowerCase().includes(searchInput) || law.fullText.toLowerCase().includes(searchInput));
            filteredLaws.forEach((law: any) => {
              law.fullText.replace(new RegExp(`(${searchInput})`, 'gi'), (match:string) => `<b>${match}</b>`)
            })
            setExpandAll(true);
            setLaws(filteredLaws);
        }
      }, 800);

  return (
    <Main>
      <Container>
      <PageBreadCrumbs></PageBreadCrumbs>
      <Typography
        variant="h5"
        align={'left'}
        data-aos={'fade-up'}
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
        paddingTop={3}>
            M.G.L. Chapter 55
        </Typography>
        <Typography  
            variant="subtitle2" 
            color="text.secondary"
            marginBottom={0}
            >
          The Massachusetts Finance Law
        </Typography>
        <Box marginTop={-2} marginBottom={1} display={'flex'} justifyContent={'right'} marginRight={3}>
          {
            !expandAll &&
            <Button size="small" startIcon={<ExpandMoreIcon />} onClick={()=>setExpandAll(true)}>
              Expand All
            </Button>
          }
          {
            expandAll &&
            <Button size="small" startIcon={<ExpandLessIcon />} onClick={()=>setExpandAll(false)}>
              Collapse All
            </Button>
          }
        </Box>
        <Divider/>
        <Grid container>
          <Grid size={{ xs: 12, md: 10 }} marginBottom={2} marginTop={2}>
            <Stack direction={"row"} spacing={1} sx={{ mb: 1}}>
                <FormControl size="small" sx={{ m: 1, p:0, minWidth: 200 }}>
                  <OutlinedInput
                      placeholder={"Search Laws..."}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      }
                      onChange={filterByName}
                  />
                </FormControl>
            </Stack>
          </Grid>
          <Alert severity="warning" icon={false}>
              <Typography variant="subtitle1" align={'left'} fontWeight={'bold'}>
                Important notice
              </Typography>
              <Typography variant='body1' align={'left'}></Typography>
              The OCPF website provides an <b>unofficial</b> version of M.G.L. Chapter 55. 
              While we make every effort to ensure the accuracy of the information provided, please do not rely on this information without first checking it against the Official Edition of the General Laws of Massachusetts.
          </Alert>
        </Grid> 
        <Grid container marginTop={2}>
          <Grid size={{xs:12, md:10}}>
            {laws?.map((document: any) => (
            <Box
              component={Accordion}
              onChange={()=>expandLaw(document.id)}
              expanded={selectedLawId === document.id || expandAll}
              key={document.id}
              paddingY={1}
              elevation={0}
              sx={{
                '&:first-of-type': {
                  borderTopLeftRadius: 1,
                  borderTopRightRadius: 1,
              },
                '&:not(:first-of-type):before': {
                  opacity: '1 !important',
                  display: 'block !important',
              },
                '&.Mui-expanded': {
                  margin: 0,
              },
            }}
          >
            <Box
              component={AccordionSummary}
              expandIcon={<ExpandMoreIcon />}
              padding={'0 !important'}
              aria-controls="law-full-text"
            >
              <Box>
                <Stack direction={'row'} spacing={1}>
                    <Typography fontWeight={550} variant='subtitle1' color='primary'>Section {document.ordinal}{document.documentSuffix && document.documentSuffix.toUpperCase()} {document.title}</Typography>
                </Stack>
              </Box>
            </Box>
            <AccordionDetails>
                <Box
                    width={1}
                    height={1}
                    padding={2}
                    component={Card}
                    boxShadow={0}
                    variant={'outlined'}
                >
                    {
                      searchInput.length === 0?
                      <div dangerouslySetInnerHTML={{ __html: document.fullText }} />
                      :
                      <div dangerouslySetInnerHTML={{ __html: document.fullText.replace(new RegExp(`(${searchInput})`, 'gi'), (match:string) => `<mark>${match}</mark>`) }} />
                    }
                </Box>
            </AccordionDetails>
            </Box>
            ))}
          </Grid>
        </Grid>

      </Container>
    </Main>
  );
};

export default CampaignFinanceLaws;